const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const CUSTOM_TIMELINE_KEY = "__custom_timeline";

export const createGroup = (definition, index = 0) => ({
  id: `${definition.id}:group:${Date.now()}:${index}`,
  duration: Math.max(0.1, number(definition.default_group_duration ?? definition.duration, 4)),
  media: {},
  children: [],
});

export const getInitialCustomTimeline = (template) => {
  const definition = template?.layers?.find((layer) => layer.type === "custom_group");
  return definition ? { definition_id: definition.id, groups: [createGroup(definition)] } : null;
};

export const removeCustomScene = (customizations, groupId) => {
  const timeline = customizations[CUSTOM_TIMELINE_KEY];
  const group = timeline?.groups?.find((scene) => scene.id === groupId);
  if (!group) return customizations;

  const next = { ...customizations };
  delete next[`${group.id}:media`];
  (group.children || []).forEach((child) => delete next[child.id]);
  next[CUSTOM_TIMELINE_KEY] = {
    ...timeline,
    groups: timeline.groups.filter((scene) => scene.id !== groupId),
  };
  return next;
};

export const buildCustomTimeline = (template, customizations = {}) => {
  const definition = template?.layers?.find((layer) => layer.type === "custom_group");
  if (!definition) return { layers: template?.layers || [], duration: number(template?.duration, 0), timeline: null };

  const timeline = customizations[CUSTOM_TIMELINE_KEY] || getInitialCustomTimeline(template);
  let cursor = 0;
  const layers = [];
  const groups = timeline?.groups || [];
  const sceneStride = Math.max(2, ...groups.map((group) => (group.children?.length || 0) + 1));
  groups.forEach((group, groupIndex) => {
    const mediaId = `${group.id}:media`;
    const mediaCustom = customizations[mediaId] || {};
    const sourceDuration = number(mediaCustom.source_duration ?? group.media?.source_duration, 0);
    const trimStart = Math.max(0, number(mediaCustom.trim_start ?? group.media?.trim_start, 0));
    const trimEnd = Math.max(0, number(mediaCustom.trim_end ?? group.media?.trim_end, 0));
    const isVideo = (mediaCustom.media_type ?? group.media?.media_type) === "video";
    const sceneSettings = { ...definition, ...group, ...group.media, ...mediaCustom };
    const duration = Math.max(0.1, isVideo && sourceDuration > 0
      ? sourceDuration - trimStart - trimEnd
      : number(group.duration, definition.default_group_duration || 4));
    // Earlier scenes must remain above later scenes while their transition
    // windows overlap (for example, to reveal the next scene under a slide).
    // Fractional offsets preserve the Custom Group's position relative to
    // ordinary template layers.
    const sceneZ = number(definition.z_index, 0)
      + ((groups.length - groupIndex) * sceneStride) / 1000000;
    const common = { group_id: group.id, start_time: cursor, duration };
    layers.push({
      ...definition,
      ...group.media,
      ...common,
      id: mediaId,
      type: "video",
      name: `Scene ${groupIndex + 1} media`,
      music_type: false,
      add_recording_capability: true,
      z_index: sceneZ,
    });
    (group.children || []).forEach((child, childIndex) => layers.push({
      ...child,
      ...common,
      id: child.id,
      z_index: sceneZ + (childIndex + 1) / 1000000,
      entrance_animation: sceneSettings.entrance_animation || "none",
      entrance_animation_duration: sceneSettings.entrance_animation_duration,
      exit_animation: sceneSettings.exit_animation || "none",
      exit_animation_duration: sceneSettings.exit_animation_duration,
    }));
    const overlap = sceneSettings.exit_animation && sceneSettings.exit_animation !== "none"
      ? Math.min(duration - 0.1, Math.max(0, number(sceneSettings.exit_animation_duration ?? sceneSettings.animation_duration, 0.5)))
      : 0;
    cursor += groupIndex < groups.length - 1 ? Math.max(0.1, duration - overlap) : duration;
  });

  const duration = Math.max(0.1, cursor);
  const outside = (template.layers || []).filter((layer) => layer.type !== "custom_group").map((layer) => ({
    ...layer,
    ...(layer.type === "video" ? { start_time: 0, duration } : {}),
  }));
  return { layers: [...outside, ...layers], duration, timeline };
};
